"use client";

import React, { useState } from "react";
import { AlertCircle, StickyNote, X } from "lucide-react";
import type { NodeNote, NoteKind } from "./types";
import { inputCls, labelCls, uid } from "./constants";

export function NotesForm({
  notes,
  onChange,
}: {
  notes: NodeNote[];
  onChange: (n: NodeNote[]) => void;
}) {
  const [text, setText] = useState("");
  const [kind, setKind] = useState<NoteKind>("note");
  const [adding, setAdding] = useState(false);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className={labelCls}>Notizen</label>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="text-[11px] text-gray-400 hover:text-gray-600"
          >
            + Hinzufügen
          </button>
        )}
      </div>
      {notes.map((n) => (
        <div
          key={n.id}
          className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${
            n.kind === "requirement"
              ? "border-blue-500/20 bg-blue-500/5"
              : "border-amber-500/20 bg-amber-500/5"
          }`}
        >
          {n.kind === "requirement" ? (
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-400" />
          ) : (
            <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
          )}
          <p className="flex-1 text-xs leading-relaxed text-gray-700 dark:text-gray-300">
            {n.text}
          </p>
          <button
            onClick={() => onChange(notes.filter((x) => x.id !== n.id))}
            className="text-gray-300 hover:text-red-400"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
      {adding && (
        <div className="dark:bg-white/3 space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-white/10">
          <div className="flex gap-1.5">
            {(["note", "requirement"] as NoteKind[]).map((k) => (
              <button
                key={k}
                onClick={() => setKind(k)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium capitalize transition ${
                  kind === k
                    ? k === "note"
                      ? "bg-amber-500/20 text-amber-500"
                      : "bg-blue-500/20 text-blue-500"
                    : "text-gray-400"
                }`}
              >
                {k === "note" ? "Notiz" : "Anforderung"}
              </button>
            ))}
          </div>
          <textarea
            autoFocus
            className={`${inputCls} resize-none`}
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Text…"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setAdding(false);
                setText("");
              }}
              className="rounded-lg px-3 py-1.5 text-xs text-gray-400"
            >
              Abbrechen
            </button>
            <button
              disabled={!text.trim()}
              onClick={() => {
                onChange([...notes, { id: uid(), kind, text: text.trim() }]);
                setAdding(false);
                setText("");
              }}
              className="rounded-lg bg-black px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40 dark:bg-white dark:text-black"
            >
              Hinzufügen
            </button>
          </div>
        </div>
      )}
      {notes.length === 0 && !adding && (
        <p className="text-xs text-gray-400">Noch keine Notizen.</p>
      )}
    </div>
  );
}
